import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import { Loader2, ShieldCheck, Clock, Lock, TrendingUp, Gift, ChevronRight, UserX, LayoutDashboard, Layers, MessageCircle, Wallet, Shield, Settings, Check, CheckCircle, Key, CheckCircle2, ArrowUpDown, ArrowUp, ArrowDown, AlertCircle, Calendar as CalendarIcon, FileText as FileTextIcon } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { parseTurkishNumber, formatTurkishCurrency } from "@/utils/numberUtils";
import {
  Users,
  Plus,
  Search,
  Star,
  Calendar,
  Percent,
  Banknote,
  Edit,
  Trash2,
  LayoutGrid,
  List,
  CreditCard,
  Info,
  Eye,
  X,
  Briefcase,
  Crown,
} from "lucide-react";
import { API } from "@/App";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale } from "@/utils/dateUtils";
import { DatePickerTR } from "@/components/ui/date-picker-tr";
import { formatDateToLocal } from "@/lib/utils";
import EmployeeScheduleModal from "./EmployeeScheduleModal";
import EmployeeTimeBlockModal from "./EmployeeTimeBlockModal";
import EmployeeCommissionReportModal from "./EmployeeCommissionReportModal";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useTranslation } from "react-i18next";
import { DateInputTR } from "@/components/ui/date-picker-tr";

export default function EmployeesModule({ businessId, subscription }) {
  const navigate = useNavigate();
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "packages", "admin", "employees", "surveys", "reports", "evaluations"]);
  const { formatPrice, currencySymbol } = useCurrency();
  const { getLocalizedBranch, getLocalizedTrainingType } = useLocalizedData();

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showEndModal, setShowEndModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [deleteEmployeeId, setDeleteEmployeeId] = useState(null);
  const [viewMode, setViewMode] = useState("list"); // "grid" veya "list"
  const [employeeCommission, setEmployeeCommission] = useState(null);
  
  // Loading states for preventing double-click
  const [isAddingEmployee, setIsAddingEmployee] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  
  // Invitation code system - KVKK uyumlu
  const [invitationCode, setInvitationCode] = useState("");
  const [validatingCode, setValidatingCode] = useState(false);
  const [validatedUser, setValidatedUser] = useState(null);
  
  // Unregistered employee mode
  const [addEmployeeMode, setAddEmployeeMode] = useState("invitation"); // "invitation" or "unregistered"
  const [unregisteredEmployee, setUnregisteredEmployee] = useState({
    name: "",
    surname: "",
    phone: "",
    email: "",
    birth_date: "",
    gender: ""
  });

  // Business settings for dynamic commission configuration
  const [businessTrainingTypes, setBusinessTrainingTypes] = useState([]);
  const [businessAcceptedCards, setBusinessAcceptedCards] = useState([]);
  const [businessSettings, setBusinessSettings] = useState(null);
  const [businessCardPrices, setBusinessCardPrices] = useState({}); // Kart fiyatları - prim validasyonu için

  // Permission settings
  const [defaultPermissions, setDefaultPermissions] = useState({});
  const [showPermissionSettings, setShowPermissionSettings] = useState(false);

  // Schedule and Time Block modals (like Trainer)
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showTimeBlockModal, setShowTimeBlockModal] = useState(false);
  const [showCommissionReportModal, setShowCommissionReportModal] = useState(false);
  const [scheduleEmployee, setScheduleEmployee] = useState(null);
  const [businessWorkingHours, setBusinessWorkingHours] = useState({});
  
  // Sorting state
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");

  // Check if user can add/edit - always true for now (subscription control disabled for testing)
  const canEdit = true; // subscription?.is_active && subscription?.plan !== 'free';

  // User search states
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);

  // New employee form
  const [newPosition, setNewPosition] = useState(t("employees:employeeLabel"));
  const [newWorkType, setNewWorkType] = useState("salary_receiving");
  const [newSalary, setNewSalary] = useState("");
  const [newStartDate, setNewStartDate] = useState(formatDateToLocal(new Date())); // İşe Başlama Tarihi
  const [newSalaryStartDate, setNewSalaryStartDate] = useState(formatDateToLocal(new Date())); // Maaş Hakediş Başlangıç Tarihi
  const [newCommissionSettings, setNewCommissionSettings] = useState({
    calculation_method: "per_lesson",
    card_commissions: {},
    package_commissions: { type: "percentage", rates: {} }
  });

  // Bonus payment form
  const [showBonusModal, setShowBonusModal] = useState(false);
  const [bonusForm, setBonusForm] = useState({
    employee_id: "",
    amount: "",
    description: "",
    payment_date: formatDateToLocal(new Date()),
    payment_type: "bonus"
  });

  // New salary entry for editing (like trainers)
  const [newSalaryAmount, setNewSalaryAmount] = useState("");
  const [salaryEffectiveDate, setSalaryEffectiveDate] = useState("");

  // Work type info
  const workTypeInfo = {
    salary_receiving: t("reports:employeeSalaryOnlyDesc"),
    salary_and_commission: t("reports:employeeSalaryCommDesc"),
    commission_only: t("reports:employeeCommOnlyDesc")
  };

  useEffect(() => {
    if (businessId) {
      fetchEmployees();
      fetchPermissionSettings();
      fetchBusinessSettings();
    }
  }, [businessId]);

  // Debounced user search
  const searchUsers = useCallback(async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setIsSearching(true);
    try {
      const response = await fetch(`${API}/auth/users/search?q=${encodeURIComponent(query)}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        const filtered = data.filter(user => !employees.some(e => e.user_id === user.user_id));
        setSearchResults(filtered);
        setShowUserDropdown(true);
      }
    } catch (error) {
      console.error("Error searching users:", error);
    } finally {
      setIsSearching(false);
    }
  }, [employees]);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      if (userSearchQuery && !selectedUser) {
        searchUsers(userSearchQuery);
      } else {
        setSearchResults([]);
        setShowUserDropdown(false);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [userSearchQuery, searchUsers, selectedUser]);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          searchInputRef.current && !searchInputRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  
  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setUserSearchQuery(`${user.name || ""} ${user.surname || ""} (${user.email})`);
    setSearchResults([]);
    setShowUserDropdown(false);
  };
  
  const getRoleLabel = (role) => {
    const labels = {
      trainer: t("common:trainer"),
      member: t("common:member"),
      business: t("common:business"),
      admin: t("admin:admin"),
      employee: t("employees:employeeLabel")
    };
    return labels[role] || role || t("common:member");
  };

  const fetchEmployees = async () => {
    try {
      const res = await fetch(`${API}/businesses/${businessId}/employees`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setEmployees(data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissionSettings = async () => {
    try {
      const res = await fetch(`${API}/businesses/${businessId}/employee-permissions`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setDefaultPermissions(data.default_permissions || {});
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchBusinessSettings = async () => {
    try {
      const [businessRes, cardPricesRes] = await Promise.all([
        fetch(`${API}/businesses/${businessId}`, { credentials: "include" }),
        fetch(`${API}/businesses/${businessId}/card-prices`, { credentials: "include" }),
      ]);
      
      if (businessRes.ok) {
        const data = await businessRes.json();
        setBusinessSettings(data);
        if (data.training_types?.length > 0) setBusinessTrainingTypes(data.training_types);
        if (data.accepted_cards?.length > 0) setBusinessAcceptedCards(data.accepted_cards);
        if (data.working_hours) setBusinessWorkingHours(data.working_hours);
      }
      if (cardPricesRes.ok) {
        const cardPricesData = await cardPricesRes.json();
        // Convert array format to object format for easy lookup
        // [{card_name: "MultiSport", single_session_price: 400}] -> {MultiSport: 400}
        const cardPricesArray = cardPricesData.card_prices || [];
        const cardPricesObj = {};
        if (Array.isArray(cardPricesArray)) {
          cardPricesArray.forEach(cp => {
            if (cp.card_name) {
              cardPricesObj[cp.card_name] = cp.single_session_price || cp.price || 0;
            }
          });
        } else if (typeof cardPricesArray === 'object') {
          // Already object format
          Object.assign(cardPricesObj, cardPricesArray);
        }
        setBusinessCardPrices(cardPricesObj);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchEmployeeCommission = async (employeeId) => {
    try {
      const res = await fetch(`${API}/businesses/${businessId}/employees/${employeeId}/commissions/summary`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setEmployeeCommission(data);
      }
    } catch (e) {
      console.error(e);
      setEmployeeCommission(null);
    }
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    
    // Prevent double-click
    if (isAddingEmployee) return;
    
    if (addEmployeeMode === "invitation") {
      // Davet kodu doğrulanmış mı kontrol et
      if (!validatedUser) { toast.error(t("admin:toast_onceDavetKodunuDogrulayin")); return; }
      if (newWorkType !== "commission_only" && (!newSalary || parseFloat(newSalary) <= 0)) {
        toast.error(t("employees:salaryRequired")); return;
      }

      setIsAddingEmployee(true);

      try {
        const cleanCommissionSettings = {
          calculation_method: newCommissionSettings.calculation_method || "per_lesson",
          commission_scope: newCommissionSettings.commission_scope || "all_lessons",
          card_commissions: {},
          package_commissions: {
            type: newCommissionSettings.package_commissions?.type || "percentage",
            rates: {}
          }
        };
        
        if (newCommissionSettings.card_commissions) {
          Object.entries(newCommissionSettings.card_commissions).forEach(([cardName, cardConfig]) => {
            const cleanRates = {};
            if (cardConfig.rates) {
              Object.entries(cardConfig.rates).forEach(([trainingType, rate]) => {
                cleanRates[trainingType] = rate === "" || rate === null || rate === undefined ? 0 : parseFloat(rate) || 0;
              });
            }
            cleanCommissionSettings.card_commissions[cardName] = {
              type: cardConfig.type || "percentage",
              rates: cleanRates
            };
          });
        }
        
        if (newCommissionSettings.package_commissions?.rates) {
          Object.entries(newCommissionSettings.package_commissions.rates).forEach(([trainingType, rate]) => {
            cleanCommissionSettings.package_commissions.rates[trainingType] = 
              rate === "" || rate === null || rate === undefined ? 0 : parseFloat(rate) || 0;
          });
        }

        const payload = {
          user_id: validatedUser.user_id,
          phone: validatedUser.phone || null,
          email: validatedUser.email || null,
          position: newPosition,
          work_type: newWorkType,
          current_salary: parseFloat(newSalary) || 0,
          start_date: newStartDate, // İşe başlama tarihi
          salary_start_date: newSalaryStartDate, // Maaş hakediş başlangıç tarihi
          commission_settings: newWorkType !== "salary_receiving" ? cleanCommissionSettings : null,
          permissions: defaultPermissions
        };

        const res = await fetch(`${API}/businesses/${businessId}/employees`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload)
        });

        if (res.ok) {
          toast.success(`${validatedUser.name || t("employees:employeeLabel")} eklendi`);
          setShowAddModal(false);
          resetAddForm();
          fetchEmployees();
        } else {
          const error = await res.json();
          toast.error(error.detail || "Eklenemedi");
        }
      } catch (e) { 
        toast.error(t('common:errorOccurred')); 
      } finally {
        setIsAddingEmployee(false);
      }
    } else {
      // Unregistered employee mode
      if (!unregisteredEmployee.name.trim()) {
        toast.error(t("employees:toast_calisanAdiZorunludur"));
        return;
      }
      if (newWorkType !== "commission_only" && (!newSalary || parseFloat(newSalary) <= 0)) {
        toast.error(t("employees:salaryRequired")); return;
      }
      
      setIsAddingEmployee(true);
      
      try {
        const cleanCommissionSettings = {
          calculation_method: newCommissionSettings.calculation_method || "per_lesson",
          commission_scope: newCommissionSettings.commission_scope || "all_lessons",
          card_commissions: {},
          package_commissions: {
            type: newCommissionSettings.package_commissions?.type || "percentage",
            rates: {}
          }
        };
        
        if (newCommissionSettings.card_commissions) {
          Object.entries(newCommissionSettings.card_commissions).forEach(([cardName, cardConfig]) => {
            const cleanRates = {};
            if (cardConfig.rates) {
              Object.entries(cardConfig.rates).forEach(([trainingType, rate]) => {
                cleanRates[trainingType] = rate === "" || rate === null || rate === undefined ? 0 : parseFloat(rate) || 0;
              });
            }
            cleanCommissionSettings.card_commissions[cardName] = {
              type: cardConfig.type || "percentage",
              rates: cleanRates
            };
          });
        }
        
        if (newCommissionSettings.package_commissions?.rates) {
          Object.entries(newCommissionSettings.package_commissions.rates).forEach(([trainingType, rate]) => {
            cleanCommissionSettings.package_commissions.rates[trainingType] = 
              rate === "" || rate === null || rate === undefined ? 0 : parseFloat(rate) || 0;
          });
        }
        
        const response = await fetch(`${API}/businesses/${businessId}/employees/add-unregistered`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            business_id: businessId,
            name: unregisteredEmployee.name.trim(),
            surname: unregisteredEmployee.surname.trim() || null,
            phone: unregisteredEmployee.phone.trim() || null,
            email: unregisteredEmployee.email.trim() || null,
            birth_date: unregisteredEmployee.birth_date || null,
            gender: unregisteredEmployee.gender || null,
            position: newPosition,
            work_type: newWorkType,
            current_salary: parseFloat(newSalary) || 0,
            start_date: newStartDate,
            salary_start_date: newSalaryStartDate,
            commission_settings: newWorkType !== "salary_receiving" ? cleanCommissionSettings : null,
          }),
        });

        const responseText = await response.text();
        let data;
        try {
          data = responseText ? JSON.parse(responseText) : {};
        } catch (e) {
          data = {};
        }

        if (response.ok) {
          toast.success(t("employees:toast_kayitOlmayanCalisanBasariylaEklendi"));
          setShowAddModal(false);
          resetAddForm();
          fetchEmployees();
        } else {
          toast.error(data.detail || t("employees:employeeCouldNotBeAdded"));
        }
      } catch (error) {
        console.error("Add unregistered employee error:", error);
        toast.error(t('common:errorOccurred'));
      } finally {
        setIsAddingEmployee(false);
      }
    }
  };

  const resetAddForm = () => {
    setUserSearchQuery(""); setSearchResults([]); setSelectedUser(null);
    setShowUserDropdown(false);
    setNewPosition(t("employees:employeeLabel")); setNewWorkType("salary_receiving");
    setNewSalary(""); setNewStartDate(formatDateToLocal(new Date()));
    setNewSalaryStartDate(formatDateToLocal(new Date()));
    setNewCommissionSettings({ calculation_method: "per_lesson", card_commissions: {}, package_commissions: { type: "percentage", rates: {} } });
    // Reset invitation code and unregistered
    setInvitationCode("");
    setValidatedUser(null);
    setAddEmployeeMode("invitation");
    setUnregisteredEmployee({
      name: "",
      surname: "",
      phone: "",
      email: "",
      birth_date: "",
      gender: ""
    });
  };
  
  // Davet kodu doğrulama (KVKK uyumlu sistem)
  const validateInvitationCode = async () => {
    if (!invitationCode || invitationCode.length !== 6) {
      toast.error(t("admin:enterSixDigitCode"));
      return;
    }
    
    setValidatingCode(true);
    try {
      const response = await fetch(`${API}/invitation/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ code: invitationCode }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setValidatedUser(data.user);
        setSelectedUser({
          user_id: data.user.user_id,
          email: data.user.email,
          name: data.user.name,
          surname: data.user.surname,
          phone: data.user.phone
        });
        toast.success(t('common:userVerified'));
      } else {
        const error = await response.json();
        toast.error(error.detail || t("admin:invalidOrExpiredCode"));
        setValidatedUser(null);
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
      setValidatedUser(null);
    } finally {
      setValidatingCode(false);
    }
  };

  const handleUpdateEmployee = async () => {
    if (!editingEmployee) return;
    
    // Prevent double-click
    if (isSavingEdit) return;
    setIsSavingEdit(true);
    
    try {
      const cleanCommissionSettings = {
        calculation_method: editingEmployee.commission_settings?.calculation_method || "per_lesson",
        commission_scope: editingEmployee.commission_settings?.commission_scope || "all_lessons",
        card_commissions: {},
        package_commissions: {
          type: editingEmployee.commission_settings?.package_commissions?.type || "percentage",
          rates: {}
        }
      };
      
      if (editingEmployee.commission_settings?.card_commissions) {
        Object.entries(editingEmployee.commission_settings.card_commissions).forEach(([cardName, cardConfig]) => {
          const cleanRates = {};
          if (cardConfig.rates) {
            Object.entries(cardConfig.rates).forEach(([trainingType, rate]) => {
              cleanRates[trainingType] = rate === "" || rate === null || rate === undefined ? 0 : parseFloat(rate) || 0;
            });
          }
          cleanCommissionSettings.card_commissions[cardName] = {
            type: cardConfig.type || "percentage",
            rates: cleanRates
          };
        });
      }
      
      if (editingEmployee.commission_settings?.package_commissions?.rates) {
        Object.entries(editingEmployee.commission_settings.package_commissions.rates).forEach(([trainingType, rate]) => {
          cleanCommissionSettings.package_commissions.rates[trainingType] = 
            rate === "" || rate === null || rate === undefined ? 0 : parseFloat(rate) || 0;
        });
      }

      const payload = {
        position: editingEmployee.position,
        work_type: editingEmployee.work_type,
        current_salary: parseFloat(editingEmployee.current_salary) || 0,
        start_date: editingEmployee.start_date,
        is_manager: editingEmployee.is_manager || false,
        is_co_owner: editingEmployee.is_co_owner || false,
        permissions: editingEmployee.permissions || {},
        commission_settings: editingEmployee.work_type !== "salary_receiving" ? cleanCommissionSettings : null,
        // Doğrudan rezervasyon onayı yetkisi
        can_approve_reservations: editingEmployee.can_approve_reservations || false
      };

      // If new salary is entered, add it to payload
      if (newSalaryAmount && salaryEffectiveDate) {
        payload.new_salary = parseTurkishNumber(newSalaryAmount);
        payload.salary_effective_date = salaryEffectiveDate;
      }

      const res = await fetch(`${API}/businesses/${businessId}/employees/${editingEmployee.employee_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success(t('employees:employeeUpdated'));
        setShowEditModal(false);
        setEditingEmployee(null);
        setNewSalaryAmount("");
        setSalaryEffectiveDate("");
        fetchEmployees();
      } else {
        const error = await res.json();
        toast.error(error.detail || t('settings:updateFailed'));
      }
    } catch (e) { 
      toast.error(t('common:errorOccurred')); 
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleToggleManager = async (employee) => {
    try {
      const res = await fetch(`${API}/businesses/${businessId}/employees/${employee.employee_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ is_manager: !employee.is_manager })
      });
      if (res.ok) {
        toast.success(employee.is_manager ? t("employees:managerPermissionRevoked") : t("employees:madeManager"));
        fetchEmployees();
      }
    } catch (e) { toast.error(t('common:errorOccurred')); }
  };

  const handleDeleteEmployee = async (id) => {
    try {
      const res = await fetch(`${API}/businesses/${businessId}/employees/${id}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (res.ok) {
        toast.success(t("employees:toast_calisanCikarildi"));
        setDeleteEmployeeId(null);
        fetchEmployees();
      }
    } catch (e) { toast.error(t('common:errorOccurred')); }
  };

  // Toggle employee active/inactive status
  const handleToggleStatus = async (employee, e) => {
    if (e) e.stopPropagation();
    
    const newStatus = employee.is_active !== false ? false : true;
    const action = newStatus ? "aktif" : "pasif";
    
    try {
      const res = await fetch(`${API}/businesses/${businessId}/employees/${employee.employee_id}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ is_active: newStatus }),
      });

      if (res.ok) {
        toast.success(t("employees:employeeActionSuccess"));
        fetchEmployees();
      } else {
        const error = await res.json();
        toast.error(error.detail || t("employees:employeeActionFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  const handleEndEmployment = async () => {
    if (!selectedEmployee) return;
    try {
      const res = await fetch(`${API}/businesses/${businessId}/employees/${selectedEmployee.employee_id}/end`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ end_date: formatDateToLocal(new Date()), reason: t("employees:employeeLeftReason") })
      });
      if (res.ok) {
        toast.success(t("employees:toast_calisanIstenAyrildiOlarakIsaretlendi"));
        setShowEndModal(false);
        setSelectedEmployee(null);
        fetchEmployees();
      }
    } catch (e) { toast.error(t('common:errorOccurred')); }
  };

  const handleAddBonus = async () => {
    if (!bonusForm.employee_id || !bonusForm.amount) {
      toast.error("Tutar girilmelidir");
      return;
    }
    try {
      const res = await fetch(`${API}/businesses/${businessId}/employees/${bonusForm.employee_id}/bonus`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          amount: parseFloat(bonusForm.amount),
          description: bonusForm.description || t("common:extraPayment"),
          payment_date: bonusForm.payment_date,
          payment_type: bonusForm.payment_type
        })
      });
      if (res.ok) {
        toast.success(t('payments:additionalPaymentSaved'));
        setShowBonusModal(false);
        setBonusForm({ employee_id: "", amount: "", description: "", payment_date: formatDateToLocal(new Date()), payment_type: "bonus" });
      } else {
        const error = await res.json();
        toast.error(error.detail || t("common:saveFailed"));
      }
    } catch (e) { toast.error(t('common:errorOccurred')); }
  };

  const filteredEmployees = employees.filter(e => 
    `${e.name || ""} ${e.surname || ""} ${e.email || ""} ${e.position || ""}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getWorkTypeLabel = (wt) => {
    if (wt === "salary_receiving") return <><Banknote className="w-3.5 h-3.5 text-green-600" /> {t("employees:salaryReceiving")}</>;
    if (wt === "salary_and_commission") return <><Star className="w-3.5 h-3.5 text-emerald-600" /> {t("reports:salaryAndCommission")}</>;
    if (wt === "commission_only") return <><TrendingUp className="w-3.5 h-3.5 text-blue-600" /> {t("reports:commissionOnly")}</>;
    return wt;
  };

  const getWorkTypeColor = (wt) => {
    if (wt === "salary_receiving") return "bg-green-100 text-green-700";
    if (wt === "salary_and_commission") return "bg-emerald-100 text-emerald-700";
    if (wt === "commission_only") return "bg-blue-100 text-blue-700";
    return "bg-grey-100";
  };

  // Sorting functions
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4 ml-1 opacity-50" />;
    return sortDirection === "asc" ? 
      <ArrowUp className="w-4 h-4 ml-1" /> : 
      <ArrowDown className="w-4 h-4 ml-1" />;
  };

  // Sort filtered employees
  const sortedEmployees = [...filteredEmployees].sort((a, b) => {
    let aVal, bVal;
    switch (sortField) {
      case "name":
        aVal = `${a.name} ${a.surname}`.toLowerCase();
        bVal = `${b.name} ${b.surname}`.toLowerCase();
        break;
      case "work_type":
        aVal = a.work_type || "";
        bVal = b.work_type || "";
        break;
      case "start_date":
        aVal = a.start_date || "";
        bVal = b.start_date || "";
        break;
      case "status":
        aVal = a.is_active ? "active" : "inactive";
        bVal = b.is_active ? "active" : "inactive";
        break;
      default:
        aVal = "";
        bVal = "";
    }
    if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-navy" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-navy">{t('employees:employees')}</h2>
          <p className="text-grey text-sm">{t("employees:employeeCount", { count: employees.length })}</p>
        </div>
        <div className="flex gap-2">
          {/* Grid/List Toggle - Two separate buttons */}
          <div className="flex border rounded-xl overflow-hidden">
            <Button 
              variant={viewMode === "list" ? "default" : "ghost"} 
              size="sm" 
              onClick={() => setViewMode("list")} 
              className={`rounded-none ${viewMode === "list" ? "bg-navy text-white" : ""}`}
            >
              <List className="w-4 h-4" />
            </Button>
            <Button 
              variant={viewMode === "grid" ? "default" : "ghost"} 
              size="sm" 
              onClick={() => setViewMode("grid")} 
              className={`rounded-none ${viewMode === "grid" ? "bg-navy text-white" : ""}`}
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
          </div>
          <Button onClick={() => setShowAddModal(true)} className="bg-navy hover:bg-navy/90 rounded-xl">
            <Plus className="w-4 h-4 mr-2" /> {t("employees:newEmployeeAdd")}
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-grey" />
        <Input
          placeholder={t("employees:ph_calisanAra")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 rounded-xl"
        />
      </div>

      {/* Grid View */}
      {viewMode === "grid" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredEmployees.map((employee) => (
            <Card
              key={employee.employee_id}
              className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/dashboard/employees/${employee.employee_id}`)}
              data-testid={`employee-card-${employee.employee_id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-12 w-12">
                      <AvatarFallback className="bg-navy text-white">
                        {employee.name?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="font-semibold text-navy">{employee.name} {employee.surname}</h3>
                      <div className="flex flex-col text-xs text-grey mt-0.5">
                        {employee.phone && <span>{employee.phone}</span>}
                        {employee.email && <span>{employee.email}</span>}
                      </div>
                      <div className="flex items-center gap-1 text-sm text-grey">
                        <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                        <span>{employee.rating?.toFixed(1) || "5.0"}</span>
                        <span className="text-xs">({employee.rating_count || 0})</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    {employee.is_manager && (
                      <Badge className="bg-purple-100 text-purple-700 text-xs">
                        <ShieldCheck className="w-3 h-3 mr-1" />
                        {t("admin:admin")}
                      </Badge>
                    )}
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      onClick={(e) => handleDeleteEmployee(employee.employee_id, e)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>

                {/* Çalışma Şekli */}
                <div className="mb-3">
                  <p className="text-xs text-grey mb-1.5">{t("common:workingType")}</p>
                  <Badge variant="secondary" className={`text-xs ${getWorkTypeColor(employee.work_type)}`}>
                    {getWorkTypeLabel(employee.work_type)}
                  </Badge>
                </div>

                {/* Pozisyon */}
                {employee.position && (
                  <div className="mb-3">
                    <p className="text-xs text-grey mb-1.5">{t('common:position')}</p>
                    <span className="text-sm">{employee.position}</span>
                  </div>
                )}

                {/* Durum */}
                <div className="flex items-center justify-between pt-2 border-t">
                  <span className="text-xs text-grey">
                    {employee.start_date ? format(new Date(employee.start_date), "dd/MM/yyyy", { locale: getDateLocale() }) : "-"}
                  </span>
                  <Badge
                    variant={employee.is_active !== false ? "success" : "secondary"}
                    className={employee.is_active !== false ? "bg-green-100 text-green-700" : "bg-grey-100 text-grey-700"}
                  >
                    {employee.is_active !== false ? t('admin:active') : t('common:passive')}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* List View - Table (Desktop) + Cards (Mobile) */}
      {viewMode === "list" && (
        <>
          {/* Desktop Table */}
          <Card className="rounded-2xl hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort("name")}
                  >
                    <span className="flex items-center">
                      {t("employees:employeeLabel")} <SortIcon field="name" />
                    </span>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort("work_type")}
                  >
                    <span className="flex items-center">
                      {t("common:workType")} <SortIcon field="work_type" />
                    </span>
                  </TableHead>
                  <TableHead>{t('business:rating')}</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort("start_date")}
                  >
                    <span className="flex items-center">
                      {t("common:start")} <SortIcon field="start_date" />
                    </span>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort("status")}
                  >
                    <span className="flex items-center">
                      {t('common:status')} <SortIcon field="status" />
                    </span>
                  </TableHead>
                  <TableHead>{t('employees:manager')}</TableHead>
                  <TableHead className="text-right">{t('common:actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedEmployees.map((employee) => (
                  <TableRow key={employee.employee_id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/dashboard/employees/${employee.employee_id}`)}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="w-8 h-8">
                          <AvatarFallback className="bg-navy/10 text-navy text-xs font-bold">
                            {(employee.name?.[0] || "E").toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-navy">{employee.name} {employee.surname}</p>
                          <div className="flex flex-col text-xs text-grey">
                            {employee.phone && <span>{employee.phone}</span>}
                            {employee.email && <span>{employee.email}</span>}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={`text-xs ${getWorkTypeColor(employee.work_type)}`}>
                        {getWorkTypeLabel(employee.work_type)}
                      </Badge>
                      <p className="text-xs text-grey mt-1">{employee.position || t("employees:employeeLabel")}</p>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        <span>{employee.rating?.toFixed(1) || "5.0"}</span>
                        <span className="text-grey text-xs">({employee.rating_count || 0})</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{employee.start_date ? format(new Date(employee.start_date), "dd/MM/yyyy", { locale: getDateLocale() }) : "-"}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={employee.is_active !== false ? "success" : "secondary"} className={employee.is_active !== false ? "bg-green-100 text-green-700" : "bg-grey-100 text-grey-700"}>
                        {employee.is_active !== false ? t('admin:active') : t('common:passive')}
                      </Badge>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Switch
                        checked={employee.is_manager || false}
                        onCheckedChange={() => handleToggleManager(employee)}
                      />
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => { 
                          setEditingEmployee({
                            ...employee,
                            is_co_owner: employee.is_co_owner || false
                          }); 
                          setShowEditModal(true); 
                        }} className="text-grey hover:text-navy">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleToggleStatus(employee, e)}
                          className={employee.is_active !== false
                            ? "text-amber-500 hover:text-amber-700 hover:bg-amber-50" 
                            : "text-green-500 hover:text-green-700 hover:bg-green-50"}
                          title={employee.is_active !== false ? "Pasif Yap" : "Aktif Yap"}
                          data-testid={`toggle-status-${employee.employee_id}`}
                        >
                          {employee.is_active !== false ? (
                            <AlertCircle className="w-4 h-4" />
                          ) : (
                            <CheckCircle2 className="w-4 h-4" />
                          )}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteEmployeeId(employee.employee_id)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        <ChevronRight className="w-4 h-4 text-grey" />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {filteredEmployees.map((employee) => (
              <Card
                key={employee.employee_id}
                className="border-0 shadow-sm cursor-pointer active:bg-slate-50"
                onClick={() => navigate(`/dashboard/employees/${employee.employee_id}`)}
                data-testid={`employee-mobile-${employee.employee_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Avatar className="h-10 w-10 flex-shrink-0">
                        <AvatarFallback className="bg-navy/10 text-navy text-sm font-bold">
                          {(employee.name?.[0] || "E").toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-navy truncate">
                            {employee.name} {employee.surname}
                          </p>
                          {employee.is_manager && (
                            <Badge className="bg-purple-100 text-purple-700 text-xs flex-shrink-0 py-0">
                              <ShieldCheck className="w-3 h-3" />
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge variant="secondary" className={`text-xs py-0 ${getWorkTypeColor(employee.work_type)}`}>
                            {getWorkTypeLabel(employee.work_type)}
                          </Badge>
                          <Badge
                            variant={employee.is_active !== false ? "default" : "secondary"}
                            className={`text-xs py-0 ${employee.is_active !== false ? "bg-green-100 text-green-700" : ""}`}
                          >
                            {employee.is_active !== false ? t('admin:active') : t('common:passive')}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => { 
                          setEditingEmployee({
                            ...employee,
                            is_co_owner: employee.is_co_owner || false
                          }); 
                          setShowEditModal(true); 
                        }}
                      >
                        <Edit className="w-4 h-4 text-grey" />
                      </Button>
                      <ChevronRight className="w-4 h-4 text-grey" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Empty State */}
      {filteredEmployees.length === 0 && (
        <Card className="rounded-2xl">
          <CardContent className="p-8 text-center">
            <Users className="w-12 h-12 mx-auto text-grey/30 mb-4" />
            <h3 className="text-lg font-semibold text-grey">{t("employees:henuzCalisanEklenmemis")}</h3>
            <p className="text-sm text-grey/70 mt-1">{t("employees:i̇lkCalisaniniziEkleyin")}</p>
            <Button onClick={() => setShowAddModal(true)} className="mt-4 bg-navy hover:bg-navy/90 rounded-xl" disabled={!canEdit}>
              <Plus className="w-4 h-4 mr-2" /> {t("employees:newEmployeeAdd")}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Detail Modal - With Tabs like TrainersModule */}
      <Dialog open={!!selectedEmployee && !showEditModal} onOpenChange={() => setSelectedEmployee(null)}>
        <DialogContent className="max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Avatar className="w-12 h-12">
                <AvatarFallback className="bg-navy text-white font-bold">
                  {(selectedEmployee?.name?.[0] || "E").toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <h3 className="font-bold text-navy">{selectedEmployee?.name} {selectedEmployee?.surname}</h3>
                <p className="text-sm text-grey font-normal">{selectedEmployee?.position || t("employees:employeeLabel")}</p>
              </div>
            </DialogTitle>
          </DialogHeader>

          {selectedEmployee && (
            <div className="space-y-4 mt-4">
              {/* Quick Action Buttons - Like TrainersModule */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Button variant="outline" size="sm" className="rounded-xl flex flex-col items-center gap-1 h-auto py-2" onClick={() => { setScheduleEmployee(selectedEmployee); setShowScheduleModal(true); }}>
                  <Clock className="w-4 h-4" />
                  <span className="text-xs">{t('settings:workingHours')}</span>
                </Button>
                <Button variant="outline" size="sm" className="rounded-xl flex flex-col items-center gap-1 h-auto py-2" onClick={() => { setScheduleEmployee(selectedEmployee); setShowTimeBlockModal(true); }}>
                  <Lock className="w-4 h-4" />
                  <span className="text-xs">Zaman Kilitle</span>
                </Button>
                <Button variant="outline" size="sm" className="rounded-xl flex flex-col items-center gap-1 h-auto py-2" onClick={() => { setScheduleEmployee(selectedEmployee); setShowCommissionReportModal(true); }}>
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-xs">Prim Raporu</span>
                </Button>
                <Button variant="outline" size="sm" className="rounded-xl flex flex-col items-center gap-1 h-auto py-2" onClick={() => { setBonusForm({ ...bonusForm, employee_id: selectedEmployee.employee_id }); setShowBonusModal(true); }}>
                  <Gift className="w-4 h-4" />
                  <span className="text-xs">{t("common:extraPayment")}</span>
                </Button>
              </div>

              {/* Çalışma Şekli */}
              <div className="p-3 bg-slate-50 rounded-xl">
                <p className="text-xs text-grey mb-1">{t("common:workingType")}</p>
                <p className="text-sm font-medium flex items-center gap-2">
                  {getWorkTypeLabel(selectedEmployee.work_type)}
                </p>
              </div>

              {/* Maaş - if applicable */}
              {selectedEmployee.work_type !== "commission_only" && selectedEmployee.current_salary > 0 && (
                <div className="p-3 bg-green-50 rounded-xl border border-green-200">
                  <p className="text-xs text-green-700 mb-1 font-medium">{t('trainers:salary')}</p>
                  <div className="flex items-center gap-2">
                    <Banknote className="w-4 h-4 text-green-600" />
                    <span className="text-lg font-bold text-green-700">{formatTurkishCurrency((selectedEmployee.current_salary || 0))}</span>
                    <span className="text-xs text-green-600">/ ay</span>
                  </div>
                </div>
              )}

              {/* Prim Tipi ve Başlangıç Tarihi - for commission types */}
              {selectedEmployee.work_type !== "salary_receiving" && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-50 rounded-xl">
                    <p className="text-xs text-grey mb-1">Prim Tipi</p>
                    <p className="text-sm font-medium flex items-center gap-2">
                      {(() => {
                        const cs = selectedEmployee.commission_settings;
                        if (cs) {
                          const types = new Set();
                          if (cs.card_commissions) {
                            Object.values(cs.card_commissions).forEach(card => {
                              if (card.type) types.add(card.type);
                            });
                          }
                          if (cs.package_commissions?.type) {
                            types.add(cs.package_commissions.type);
                          }
                          
                          if (types.has("percentage") && types.has("fixed")) {
                            return <><Percent className="w-3.5 h-3.5 text-navy" /><Banknote className="w-3.5 h-3.5 text-navy" /> {t("common:percentageAndFixed")}</>;
                          } else if (types.has("percentage")) {
                            return <><Percent className="w-3.5 h-3.5 text-navy" /> {t("common:percentageBased")}</>;
                          } else if (types.has("fixed")) {
                            return <><Banknote className="w-3.5 h-3.5 text-navy" /> Sabit ({currencySymbol})</>;
                          }
                        }
                        return t("common:notDefined");
                      })()}
                    </p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-xl">
                    <p className="text-xs text-grey mb-1">{t("common:startDateWork")}</p>
                    <p className="text-sm font-medium text-navy">
                      {selectedEmployee.start_date 
                        ? format(new Date(selectedEmployee.start_date), "d MMM yyyy", { locale: getDateLocale() })
                        : "-"}
                    </p>
                  </div>
                </div>
              )}

              {/* İşe Başlama Tarihi - for salary only types */}
              {selectedEmployee.work_type === "salary_receiving" && (
                <div className="p-3 bg-slate-50 rounded-xl">
                  <p className="text-xs text-grey mb-1">{t("common:startDateWork")}</p>
                  <p className="text-sm font-medium text-navy">
                    {selectedEmployee.start_date 
                      ? format(new Date(selectedEmployee.start_date), "d MMM yyyy", { locale: getDateLocale() })
                      : "-"}
                  </p>
                </div>
              )}

              {/* Prim bilgileri - if work_type includes commission */}
              {selectedEmployee.work_type !== "salary_receiving" && (
                <>
                  <div className="p-3 bg-blue-50 rounded-xl">
                    <p className="text-xs text-grey mb-1">{t('trainers:commissionCalculation')}</p>
                    <p className="text-sm font-medium text-navy">
                      {selectedEmployee.commission_settings?.calculation_method === "per_package_sale" ? `📦 ${t("packages:perPackageSaleMethod")}` : `📚 ${t("reports:perLessonCommMethod")}`}
                    </p>
                  </div>

                  {/* Bu Ay Prim Özeti */}
                  <div className="p-3 bg-purple-50 rounded-xl">
                    <div className="flex justify-between items-center mb-2">
                      <p className="text-xs font-medium text-purple-700">{t("reports:thisMonthCommissionSummary")}</p>
                      <Badge variant="secondary" className="bg-purple-100 text-purple-700 text-xs">
                        {new Date().toLocaleString('tr-TR', { month: 'long', year: 'numeric' })}
                      </Badge>
                    </div>
                    {employeeCommission ? (
                      <div className="grid grid-cols-3 gap-2">
                        <div className="text-center p-1.5 bg-white rounded-lg">
                          <p className="text-xs text-grey">{t("common:i̇slem")}</p>
                          <p className="text-base font-bold text-navy">{employeeCommission.total_sessions || 0}</p>
                        </div>
                        <div className="text-center p-1.5 bg-white rounded-lg">
                          <p className="text-xs text-grey">{t('common:total')}</p>
                          <p className="text-base font-bold text-purple-700">{formatTurkishCurrency((employeeCommission.total_commission || 0))}</p>
                        </div>
                        <div className="text-center p-1.5 bg-white rounded-lg">
                          <p className="text-xs text-grey">Bekleyen</p>
                          <p className="text-base font-bold text-orange-600">{formatTurkishCurrency((employeeCommission.pending_amount || 0))}</p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-grey text-center py-1">{t('common:loading')}</p>
                    )}
                  </div>

                  {/* Prim Oranları */}
                  {selectedEmployee.commission_settings && (
                    <div>
                      <p className="text-xs text-grey mb-1">{t("reports:commissionRates")}</p>
                      <div className="space-y-2">
                        {/* Card Commissions */}
                        {businessAcceptedCards.length > 0 && selectedEmployee.commission_settings.card_commissions && (
                          <div>
                            <p className="text-xs font-medium text-grey-600 mb-1 flex items-center gap-1">
                              <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                              {t("reports:cardCommissions")}
                            </p>
                            <div className="space-y-1">
                              {businessAcceptedCards.map(cardName => {
                                const cardConfig = selectedEmployee.commission_settings?.card_commissions?.[cardName];
                                if (!cardConfig) return null;
                                return (
                                  <div key={cardName} className="p-1.5 bg-amber-50 rounded-lg">
                                    <p className="text-xs font-medium text-amber-700 mb-0.5">{cardName} ({cardConfig.type === "percentage" ? "%" : currencySymbol})</p>
                                    <div className="flex flex-wrap gap-1">
                                      {businessTrainingTypes.map(type => (
                                        <span key={type} className="text-xs bg-amber-100 px-1.5 py-0.5 rounded">
                                          {type}: {cardConfig.type === "percentage" ? "%" : currencySymbol}{cardConfig.rates?.[type] || 0}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Package Commissions */}
                        {selectedEmployee.commission_settings.package_commissions && (
                          <div>
                            <p className="text-xs font-medium text-grey-600 mb-1 flex items-center gap-1">
                              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                              {t("packages:packageCommissions")} ({selectedEmployee.commission_settings.package_commissions.type === "percentage" ? "%" : currencySymbol})
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {businessTrainingTypes.map(type => (
                                <span key={type} className="text-xs bg-emerald-100 px-1.5 py-0.5 rounded text-emerald-700">
                                  {type}: {selectedEmployee.commission_settings.package_commissions.type === "percentage" ? "%" : currencySymbol}{selectedEmployee.commission_settings.package_commissions.rates?.[type] || 0}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Bottom Actions */}
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1 rounded-xl" onClick={() => { 
                  setEditingEmployee({
                    ...selectedEmployee,
                    is_co_owner: selectedEmployee.is_co_owner || false
                  }); 
                  setShowEditModal(true); 
                  setSelectedEmployee(null); 
                }}>
                  <Edit className="w-4 h-4 mr-2" /> {t("common:edit")}
                </Button>
                <Button variant="outline" className="rounded-xl text-red-600 hover:bg-red-50" onClick={() => { setShowEndModal(true); }}>
                  <UserX className="w-4 h-4 mr-2" /> {t("common:left")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Modal - With Tabs */}
      <Dialog open={showEditModal} onOpenChange={(open) => { setShowEditModal(open); if (!open) setEditingEmployee(null); }}>
        <DialogContent className="max-w-xl rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('employees:editEmployee')}</DialogTitle>
          </DialogHeader>
          
          {editingEmployee && (
            <Tabs defaultValue="info" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="info">Bilgiler</TabsTrigger>
                <TabsTrigger value="commission">Prim</TabsTrigger>
                <TabsTrigger value="permissions">{t("common:i̇zinler")}</TabsTrigger>
              </TabsList>

              {/* Bilgiler Tab */}
              <TabsContent value="info" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t('common:firstName')}</Label>
                    <Input value={editingEmployee.name || ""} disabled className="rounded-xl mt-1 bg-slate-50" />
                  </div>
                  <div>
                    <Label>Soyisim</Label>
                    <Input value={editingEmployee.surname || ""} disabled className="rounded-xl mt-1 bg-slate-50" />
                  </div>
                </div>

                <div>
                  <Label>{t('common:position')}</Label>
                  <Input value={editingEmployee.position || ""} onChange={(e) => setEditingEmployee({...editingEmployee, position: e.target.value})} className="rounded-xl mt-1" />
                </div>

                {/* İşe Başlama Tarihi */}
                <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                  <h4 className="font-semibold text-green-700 flex items-center gap-2 mb-3">
                    <Calendar className="w-4 h-4" /> {t("common:workStartDate")}
                  </h4>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-grey">Mevcut:</span>
                    <span className="font-medium text-green-700">
                      {editingEmployee.start_date 
                        ? format(new Date(editingEmployee.start_date), "dd/MM/yyyy", { locale: getDateLocale() })
                        : t("common:notSpecified")}
                    </span>
                  </div>
                  <div className="mt-3">
                    <Label className="text-sm">Yeni Tarih Gir</Label>
                    <DatePickerTR
                      value={editingEmployee.start_date ? new Date(editingEmployee.start_date) : null}
                      onChange={(date) => setEditingEmployee({...editingEmployee, start_date: date ? formatDateToLocal(date) : ""})}
                      placeholder={t('common:selectDate')}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label>{t("common:workingType")}</Label>
                  <Select value={editingEmployee.work_type || "salary_receiving"} onValueChange={(v) => setEditingEmployee({...editingEmployee, work_type: v})}>
                    <SelectTrigger className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="salary_receiving">💵 {t("employees:salaryReceiving")}</SelectItem>
                      <SelectItem value="salary_and_commission">💎 {t("reports:salaryAndCommission")}</SelectItem>
                      <SelectItem value="commission_only">💰 {t("employees:commissionOnly")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Maaş Ayarları - Antrenör gibi */}
                {editingEmployee.work_type !== "commission_only" && (
                  <div className="p-4 bg-blue-50 rounded-xl space-y-3 border border-blue-200">
                    <h4 className="font-semibold text-blue-700 flex items-center gap-2">
                      <Wallet className="w-4 h-4" /> {t("employees:salarySettings")}
                    </h4>
                    <div className="flex items-center justify-between p-2 bg-white rounded-lg">
                      <span className="text-sm text-grey">{t("employees:currentSalary")}</span>
                      <span className="font-bold text-blue-600">{formatTurkishCurrency((editingEmployee.current_salary || 0))}</span>
                    </div>
                    {editingEmployee.salary_start_date && (
                      <div className="flex items-center justify-between p-2 bg-white rounded-lg">
                        <span className="text-sm text-grey">{t("common:accruedStart")}</span>
                        <span className="font-medium text-blue-600">
                          {format(new Date(editingEmployee.salary_start_date), "dd/MM/yyyy", { locale: getDateLocale() })}
                        </span>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-sm">{t("employees:newSalary")}</Label>
                        <Input 
                          type="number" 
                          value={newSalaryAmount} 
                          onChange={(e) => setNewSalaryAmount(e.target.value)} 
                          placeholder={t("employees:newSalaryAmount")} 
                          className="rounded-xl mt-1" 
                        />
                      </div>
                      <div>
                        <Label className="text-sm">{t("common:validityDate")}</Label>
                        <DatePickerTR 
                          value={salaryEffectiveDate ? new Date(salaryEffectiveDate) : null} 
                          onChange={(date) => setSalaryEffectiveDate(date ? formatDateToLocal(date) : "")} 
                          placeholder={t('common:selectDate')} 
                          className="mt-1" 
                        />
                      </div>
                    </div>
                    <p className="text-xs text-blue-600">{t("employees:yeniMaasGirildiktenSonraHakedisler")}</p>
                  </div>
                )}

                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-xl">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-amber-600" />
                    <div>
                      <p className="font-medium text-amber-800">{t('employees:manager')}</p>
                      <p className="text-xs text-amber-600">{t("common:fullMenuAccess")}</p>
                    </div>
                  </div>
                  <Switch 
                    checked={editingEmployee.is_manager || false} 
                    onCheckedChange={(c) => {
                      setEditingEmployee(prev => {
                        const newPermissions = c 
                          ? prev.permissions 
                          : {
                              ...prev.permissions,
                              branches: false,
                              analytics: false,
                              subscription: false,
                              salary_payments: false,
                              bonus_payments: false,
                              business_expenses: false,
                              trainer_collections: false,
                              commissions: false,
                              reports: false,
                              rooms: false,
                              packages: false,
                              price_list: false,
                              trainers: false,
                              employees: false,
                              settings: false
                            };
                        return {
                          ...prev,
                          is_manager: c,
                          permissions: newPermissions
                        };
                      });
                    }} 
                  />
                </div>
              </TabsContent>

              {/* Prim Tab */}
              <TabsContent value="commission" className="space-y-4 mt-4">
                {editingEmployee.work_type === "salary_receiving" ? (
                  <div className="text-center py-8 text-grey">
                    <Banknote className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>{t("reports:employeeSalaryOnlyNoCommission")}</p>
                  </div>
                ) : (
                  <>
                    <div className="p-3 bg-blue-50 rounded-xl space-y-2">
                      <Label className="font-medium text-blue-800">{t('trainers:commissionCalculation')}</Label>
                      <div className="flex flex-col gap-2">
                        <label className="flex items-start gap-3 p-2 bg-white rounded-lg cursor-pointer border text-sm">
                          <input type="radio" checked={editingEmployee.commission_settings?.calculation_method !== "per_package_sale"} onChange={() => setEditingEmployee({...editingEmployee, commission_settings: {...editingEmployee.commission_settings, calculation_method: "per_lesson"}})} className="mt-0.5" />
                          <div><p className="font-medium">📚 {t("common:perLessonLabel")}</p></div>
                        </label>
                        <label className="flex items-start gap-3 p-2 bg-white rounded-lg cursor-pointer border text-sm">
                          <input type="radio" checked={editingEmployee.commission_settings?.calculation_method === "per_package_sale"} onChange={() => setEditingEmployee({...editingEmployee, commission_settings: {...editingEmployee.commission_settings, calculation_method: "per_package_sale", commission_scope: "all_lessons"}})} className="mt-0.5" />
                          <div><p className="font-medium">📦 {t("packages:perPackageSale")}</p></div>
                        </label>
                        {editingEmployee.commission_settings?.calculation_method === "per_package_sale" && (
                          <div className="ml-6 p-2 bg-blue-100 rounded-lg">
                            <p className="text-xs text-blue-700">{t("packages:employeeReceivesCommFromAllSales")}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Card Commissions */}
                    {businessAcceptedCards.length > 0 && (
                      <div className="space-y-2">
                        <Label className="font-medium text-amber-800 text-sm flex items-center gap-2">
                          <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                          {t("reports:cardCommissions")}
                        </Label>
                        {businessAcceptedCards.map(cardName => {
                          const cardConfig = editingEmployee.commission_settings?.card_commissions?.[cardName] || { type: "percentage", rates: {} };
                          const cardPrice = businessCardPrices[cardName] || 0;
                          return (
                            <div key={cardName} className="p-3 bg-amber-50 rounded-xl space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-amber-700 text-sm">{cardName}</span>
                                <Select value={cardConfig.type || "percentage"} onValueChange={(v) => setEditingEmployee({...editingEmployee, commission_settings: {...editingEmployee.commission_settings, card_commissions: {...editingEmployee.commission_settings?.card_commissions, [cardName]: { ...cardConfig, type: v }}}})}>
                                  <SelectTrigger className="w-28 h-8 rounded-lg text-sm"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="percentage">{t("common:percentage")}</SelectItem>
                                    <SelectItem value="fixed">{t("common:fixedAmount")}</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              {/* Kart fiyatı uyarısı - sabit prim için */}
                              {cardConfig.type === "fixed" && cardPrice <= 0 && (
                                <p className="text-xs text-red-500 bg-red-50 p-2 rounded">
                                  ⚠️ {t("common:cardPriceNotSet")}
                                </p>
                              )}
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                {businessTrainingTypes.map(trainingType => {
                                  const maxFixedValue = cardConfig.type === "fixed" ? cardPrice : undefined;
                                  const currentValue = cardConfig.rates?.[trainingType] || "";
                                  const isOverMax = cardConfig.type === "fixed" && cardPrice > 0 && parseFloat(currentValue) > cardPrice;
                                  
                                  return (
                                  <div key={trainingType}>
                                    <Label className="text-xs text-amber-600">{trainingType}</Label>
                                    <Input
                                      type="number"
                                      min="0"
                                      max={cardConfig.type === "percentage" ? 100 : maxFixedValue}
                                      step="1"
                                      value={currentValue}
                                      onChange={(e) => {
                                        let value = e.target.value === "" ? "" : parseFloat(e.target.value) || 0;
                                        // Yüzde için max 100
                                        if (cardConfig.type === "percentage" && value > 100) {
                                          value = 100;
                                        }
                                        // Sabit için max kart fiyatı
                                        if (cardConfig.type === "fixed" && cardPrice > 0 && value > cardPrice) {
                                          value = cardPrice;
                                          toast.error(t("reports:commissionExceedsCardPrice"));
                                        }
                                        setEditingEmployee({...editingEmployee, commission_settings: {...editingEmployee.commission_settings, card_commissions: {...editingEmployee.commission_settings?.card_commissions, [cardName]: {...cardConfig, rates: {...cardConfig.rates, [trainingType]: value}}}}});
                                      }}
                                      onKeyDown={(e) => {
                                        // Block non-numeric keys except navigation and decimal
                                        if (!/[\d.\-]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
                                          e.preventDefault();
                                        }
                                      }}
                                      className={`rounded-lg h-8 text-sm ${isOverMax ? 'border-red-500' : ''}`}
                                      placeholder={cardConfig.type === "fixed" ? (cardPrice > 0 ? `Max ${currencySymbol}${cardPrice}` : currencySymbol) : "% (0-100)"}
                                      disabled={cardConfig.type === "fixed" && cardPrice <= 0}
                                    />
                                    {isOverMax && (
                                      <p className="text-xs text-red-500 mt-0.5">Max: {currencySymbol}{cardPrice}</p>
                                    )}
                                  </div>
                                )})}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Package Commissions */}
                    <div className="space-y-2">
                      <Label className="font-medium text-emerald-800 text-sm flex items-center gap-2">
                        <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                        {t("packages:packageCommissions")}
                      </Label>
                      <div className="p-3 bg-emerald-50 rounded-xl space-y-2">
                        <Select value={editingEmployee.commission_settings?.package_commissions?.type || "percentage"} onValueChange={(v) => setEditingEmployee({...editingEmployee, commission_settings: {...editingEmployee.commission_settings, package_commissions: {...editingEmployee.commission_settings?.package_commissions, type: v}}})}>
                          <SelectTrigger className="w-full h-8 rounded-lg text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percentage">{t("common:percentage")}</SelectItem>
                            <SelectItem value="fixed">{t("common:fixedAmount")}</SelectItem>
                          </SelectContent>
                        </Select>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {businessTrainingTypes.map(type => (
                            <div key={type}>
                              <Label className="text-xs text-emerald-600">{type}</Label>
                              <Input
                                type="number"
                                min="0"
                                step="1"
                                value={editingEmployee.commission_settings?.package_commissions?.rates?.[type] || ""}
                                onChange={(e) => {
                                  const value = e.target.value === "" ? "" : parseFloat(e.target.value) || 0;
                                  setEditingEmployee({...editingEmployee, commission_settings: {...editingEmployee.commission_settings, package_commissions: {...editingEmployee.commission_settings?.package_commissions, rates: {...editingEmployee.commission_settings?.package_commissions?.rates, [type]: value}}}});
                                }}
                                onKeyDown={(e) => {
                                  // Block non-numeric keys except navigation and decimal
                                  if (!/[\d.\-]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
                                    e.preventDefault();
                                  }
                                }}
                                className="rounded-lg h-8 text-sm"
                                placeholder={editingEmployee.commission_settings?.package_commissions?.type === "fixed" ? currencySymbol : "%"}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </TabsContent>

              {/* İzinler Tab */}
              <TabsContent value="permissions" className="space-y-4 mt-4">
                {/* Şirket Ortağı (Kurucu Ortak) Şalteri */}
                <div className={`p-4 rounded-xl border-2 mb-3 ${editingEmployee.is_co_owner ? 'bg-emerald-50 border-emerald-300' : 'bg-slate-50 border-slate-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${editingEmployee.is_co_owner ? 'bg-emerald-600' : 'bg-slate-300'}`}>
                        <Crown className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <span className="font-semibold text-navy flex items-center gap-2">
                          Şirket Ortağı (Tam Yetki)
                        </span>
                        <p className="text-xs text-grey">
                          {editingEmployee.is_co_owner 
                            ? "Ana işletme sahibi gibi tüm finans, kasa, prim, çalışan ve yönetim ekranlarına tam erişim sağlar." 
                            : "Şirket ortağı olarak atanmamış."}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={editingEmployee.is_co_owner || false}
                      onCheckedChange={(c) => {
                        setEditingEmployee(prev => ({
                          ...prev,
                          is_co_owner: c,
                          is_manager: c ? true : prev.is_manager, // Ortak ise yönetici de otomatik açık olsun
                        }));
                      }}
                    />
                  </div>
                </div>

                {/* Yönetici Durumu */}
                <div className={`p-4 rounded-xl border-2 ${editingEmployee.is_manager ? 'bg-green-50 border-green-300' : 'bg-slate-50 border-slate-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${editingEmployee.is_manager ? 'bg-green-500' : 'bg-slate-300'}`}>
                        <Shield className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <span className="font-semibold text-navy">{t('employees:manager')}</span>
                        <p className="text-xs text-grey">
                          {editingEmployee.is_manager 
                            ? t("employees:hasManagerPermissions") 
                            : t("employees:notManagerLimited")}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={editingEmployee.is_manager || false}
                      onCheckedChange={(c) => {
                        if (!c) {
                          // Yönetici kapatılınca yönetici-only izinleri kapat
                          setEditingEmployee({
                            ...editingEmployee, 
                            is_manager: false,
                            permissions: {
                              ...editingEmployee.permissions,
                              branches: false,
                              analytics: false,
                              subscription: false,
                              salary_payments: false,
                              bonus_payments: false,
                              business_expenses: false,
                              trainer_collections: false,
                              commissions: false,
                              reports: false,
                              rooms: false,
                              packages: false,
                              price_list: false,
                              trainers: false,
                              employees: false,
                              settings: false,
                            }
                          });
                        } else {
                          setEditingEmployee({...editingEmployee, is_manager: true});
                        }
                      }}
                    />
                  </div>
                </div>

                {/* Yönetici değilse uyarı */}
                {!editingEmployee.is_manager && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                    <p className="text-xs text-amber-800 flex items-center gap-2">
                      <Lock className="w-3 h-3" />
                      <span><strong>{t("employees:managerRequiredForBadge")}</strong></span>
                    </p>
                  </div>
                )}

                {/* İzin Şablonları */}
                <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                  <Label className="text-sm font-medium text-navy mb-2 block">{t("employees:permissionTemplates")}</Label>
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="text-xs rounded-lg bg-white hover:bg-green-50 hover:border-green-300"
                      disabled={!editingEmployee.is_manager}
                      onClick={() => {
                        const allPerms = {
                          dashboard: true, dashboard_overview: true, branches: true, analytics: true, subscription: true,
                          members: true, members_list: true, qr_scan: true, measurements: true,
                          reservations: true,
                          finances: true, payments: true, salary_payments: true, bonus_payments: true,
                          business_expenses: true, trainer_collections: true, commissions: true, reports: true,
                          rooms: true, packages: true, price_list: true, trainers: true, employees: true,
                          settings: true,
                          can_view_revenue: true
                        };
                        setEditingEmployee({...editingEmployee, permissions: allPerms});
                      }}
                    >
                      👑 {t("employees:fullPermissions")} {!editingEmployee.is_manager && `(${t("employees:managerRequired")})`}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="text-xs rounded-lg bg-white hover:bg-blue-50 hover:border-blue-300"
                      onClick={() => {
                        const dersPerms = {
                          dashboard: true, dashboard_overview: true, branches: false, analytics: false, subscription: false,
                          members: true, members_list: true, qr_scan: true, measurements: true,
                          reservations: true,
                          finances: false, payments: false, salary_payments: false, bonus_payments: false,
                          business_expenses: false, trainer_collections: false, commissions: false, reports: false,
                          rooms: false, packages: false, price_list: false, trainers: false, employees: false,
                          settings: false,
                          can_view_revenue: false
                        };
                        setEditingEmployee({...editingEmployee, permissions: dersPerms});
                      }}
                    >
                      📚 Sadece Ders
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="text-xs rounded-lg bg-white hover:bg-red-50 hover:border-red-300"
                      onClick={() => {
                        const noPerms = {
                          dashboard: false, dashboard_overview: false, branches: false, analytics: false, subscription: false,
                          members: false, members_list: false, qr_scan: false, measurements: false,
                          reservations: false,
                          finances: false, payments: false, salary_payments: false, bonus_payments: false,
                          business_expenses: false, trainer_collections: false, commissions: false, reports: false,
                          rooms: false, packages: false, price_list: false, trainers: false, employees: false,
                          settings: false,
                          can_view_revenue: false
                        };
                        setEditingEmployee({...editingEmployee, permissions: noPerms});
                      }}
                    >
                      🚫 {t("common:disableAll")}
                    </Button>
                  </div>
                </div>

                {/* Genel */}
                <div className="border rounded-xl p-3 space-y-2">
                  <div className="flex items-center gap-2 mb-2">
                    <LayoutDashboard className="w-4 h-4 text-navy" />
                    <span className="font-medium text-navy">Genel</span>
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between p-2 bg-green-50 rounded-lg text-sm">
                      <span className="text-green-700">Harita</span>
                      <span className="text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded">{t("common:public")}</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">{t("common:overview")}</span>
                      <Switch
                        checked={editingEmployee.permissions?.dashboard_overview !== false}
                        onCheckedChange={(c) => setEditingEmployee({
                          ...editingEmployee, 
                          permissions: {...editingEmployee.permissions, dashboard_overview: c}
                        })}
                      />
                    </div>
                    <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingEmployee.is_manager ? 'opacity-50' : ''}`}>
                      <span className="text-grey flex items-center gap-1">
                        {t("common:branchesLabel")}
                        <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                      </span>
                      <Switch
                        checked={editingEmployee.permissions?.branches || false}
                        disabled={!editingEmployee.is_manager}
                        onCheckedChange={(c) => setEditingEmployee({
                          ...editingEmployee, 
                          permissions: {...editingEmployee.permissions, branches: c}
                        })}
                      />
                    </div>
                    <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingEmployee.is_manager ? 'opacity-50' : ''}`}>
                      <span className="text-grey flex items-center gap-1">
                        Analitik
                        <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                      </span>
                      <Switch
                        checked={editingEmployee.permissions?.analytics || false}
                        disabled={!editingEmployee.is_manager}
                        onCheckedChange={(c) => setEditingEmployee({
                          ...editingEmployee, 
                          permissions: {...editingEmployee.permissions, analytics: c}
                        })}
                      />
                    </div>
                    <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingEmployee.is_manager ? 'opacity-50' : ''}`}>
                      <span className="text-grey flex items-center gap-1">
                        Abonelik
                        <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                      </span>
                      <Switch
                        checked={editingEmployee.permissions?.subscription || false}
                        disabled={!editingEmployee.is_manager}
                        onCheckedChange={(c) => setEditingEmployee({
                          ...editingEmployee, 
                          permissions: {...editingEmployee.permissions, subscription: c}
                        })}
                      />
                    </div>
                  </div>
                </div>

                {/* Üye Yönetimi */}
                <div className="border rounded-xl p-3 space-y-2">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-navy" />
                    <span className="font-medium text-navy">{t("common:memberManagement")}</span>
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">{t('members:title')}</span>
                      <Switch
                        checked={editingEmployee.permissions?.members_list !== false}
                        onCheckedChange={(c) => setEditingEmployee({
                          ...editingEmployee, 
                          permissions: {...editingEmployee.permissions, members_list: c}
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">{t("surveys:memberForms")}</span>
                      <Switch
                        checked={editingEmployee.permissions?.member_forms || false}
                        onCheckedChange={(c) => setEditingEmployee({
                          ...editingEmployee, 
                          permissions: {...editingEmployee.permissions, member_forms: c}
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">QR Tarama</span>
                      <Switch
                        checked={editingEmployee.permissions?.qr_scan || false}
                        onCheckedChange={(c) => setEditingEmployee({
                          ...editingEmployee, 
                          permissions: {...editingEmployee.permissions, qr_scan: c}
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">{t("evaluations:measurements")}</span>
                      <Switch
                        checked={editingEmployee.permissions?.measurements || false}
                        onCheckedChange={(c) => setEditingEmployee({
                          ...editingEmployee, 
                          permissions: {...editingEmployee.permissions, measurements: c}
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">{t('members:allPackages')}</span>
                      <Switch
                        checked={editingEmployee.permissions?.all_packages || false}
                        onCheckedChange={(c) => setEditingEmployee({
                          ...editingEmployee, 
                          permissions: {...editingEmployee.permissions, all_packages: c}
                        })}
                      />
                    </div>
                  </div>
                </div>

                {/* Rezervasyonlar */}
                <div className="border rounded-xl p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-navy" />
                      <span className="font-medium text-navy">{t('common:reservations')}</span>
                    </div>
                    <Switch
                      checked={editingEmployee.permissions?.reservations !== false}
                      onCheckedChange={(c) => setEditingEmployee({
                        ...editingEmployee, 
                        permissions: {...editingEmployee.permissions, reservations: c}
                      })}
                    />
                  </div>
                  {/* Rezervasyon Onayı Yapabilir - Üye onayını atlayabilir */}
                  <div className="flex items-center justify-between p-2 bg-green-50 rounded-lg text-sm">
                    <span className="text-grey flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      {t("admin:directApprovalAuth")}
                      <span className="text-xs text-green-600 bg-green-100 px-1 rounded">{t("admin:skipMemberApproval")}</span>
                    </span>
                    <Switch
                      checked={editingEmployee.can_approve_reservations || false}
                      onCheckedChange={(c) => setEditingEmployee({
                        ...editingEmployee, 
                        can_approve_reservations: c
                      })}
                    />
                  </div>
                </div>

                {/* Ödemeler - Ana toggle yok, Paket Ödemeleri herkes için açık, diğerleri yönetici-only */}
                <div className="border rounded-xl p-3 space-y-2">
                  <div className="flex items-center gap-2 mb-2">
                    <Wallet className="w-4 h-4 text-navy" />
                    <span className="font-medium text-navy">{t('common:payments')}</span>
                  </div>
                  <div className="space-y-1.5">
                    {/* Paket Ödemeleri - Herkes için */}
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">{t("packages:packagePayments")}</span>
                      <Switch
                        checked={editingEmployee.permissions?.payments || false}
                        onCheckedChange={(c) => setEditingEmployee({
                          ...editingEmployee, 
                          permissions: {...editingEmployee.permissions, payments: c}
                        })}
                      />
                    </div>
                    {/* Maaş Ödemeleri - Yönetici only */}
                    <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingEmployee.is_manager ? 'opacity-50' : ''}`}>
                      <span className="text-grey flex items-center gap-1">
                        {t("employees:salaryPaymentsLabel")}
                        <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                      </span>
                      <Switch
                        checked={editingEmployee.permissions?.salary_payments || false}
                        disabled={!editingEmployee.is_manager}
                        onCheckedChange={(c) => setEditingEmployee({
                          ...editingEmployee, 
                          permissions: {...editingEmployee.permissions, salary_payments: c}
                        })}
                      />
                    </div>
                    {/* Ek Ödemeler - Yönetici only */}
                    <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingEmployee.is_manager ? 'opacity-50' : ''}`}>
                      <span className="text-grey flex items-center gap-1">
                        {t("common:extraPaymentsLabel")}
                        <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                      </span>
                      <Switch
                        checked={editingEmployee.permissions?.bonus_payments || false}
                        disabled={!editingEmployee.is_manager}
                        onCheckedChange={(c) => setEditingEmployee({
                          ...editingEmployee, 
                          permissions: {...editingEmployee.permissions, bonus_payments: c}
                        })}
                      />
                    </div>
                    {/* İşletme Giderleri - Yönetici only */}
                    <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingEmployee.is_manager ? 'opacity-50' : ''}`}>
                      <span className="text-grey flex items-center gap-1">
                        {t("reports:businessExpensesPermLabel")}
                        <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                      </span>
                      <Switch
                        checked={editingEmployee.permissions?.business_expenses || false}
                        disabled={!editingEmployee.is_manager}
                        onCheckedChange={(c) => setEditingEmployee({
                          ...editingEmployee, 
                          permissions: {...editingEmployee.permissions, business_expenses: c}
                        })}
                      />
                    </div>
                    {/* Gelen Kira/Prim - Yönetici only */}
                    <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingEmployee.is_manager ? 'opacity-50' : ''}`}>
                      <span className="text-grey flex items-center gap-1">
                        Gelen Kira/Prim
                        <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                      </span>
                      <Switch
                        checked={editingEmployee.permissions?.trainer_collections || false}
                        disabled={!editingEmployee.is_manager}
                        onCheckedChange={(c) => setEditingEmployee({
                          ...editingEmployee, 
                          permissions: {...editingEmployee.permissions, trainer_collections: c}
                        })}
                      />
                    </div>
                    {/* Primler - Yönetici only */}
                    <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingEmployee.is_manager ? 'opacity-50' : ''}`}>
                      <span className="text-grey flex items-center gap-1">
                        Primler
                        <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                      </span>
                      <Switch
                        checked={editingEmployee.permissions?.commissions || false}
                        disabled={!editingEmployee.is_manager}
                        onCheckedChange={(c) => setEditingEmployee({
                          ...editingEmployee, 
                          permissions: {...editingEmployee.permissions, commissions: c}
                        })}
                      />
                    </div>
                    {/* Raporlar - Yönetici only */}
                    <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingEmployee.is_manager ? 'opacity-50' : ''}`}>
                      <span className="text-grey flex items-center gap-1">
                        Raporlar
                        <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                      </span>
                      <Switch
                        checked={editingEmployee.permissions?.reports || false}
                        disabled={!editingEmployee.is_manager}
                        onCheckedChange={(c) => setEditingEmployee({
                          ...editingEmployee, 
                          permissions: {...editingEmployee.permissions, reports: c}
                        })}
                      />
                    </div>
                  </div>
                </div>

                {/* İşletme Ayarları */}
                <div className={`border rounded-xl p-3 space-y-2 ${!editingEmployee.is_manager ? 'border-amber-200 bg-amber-50/30' : 'border-amber-200 bg-amber-50/30'}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-navy flex items-center gap-2">
                      <Layers className="w-4 h-4" />
                      {t("common:businessSettings")}
                      <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                    </span>
                    <Switch
                      checked={editingEmployee.permissions?.settings || false}
                      disabled={!editingEmployee.is_manager}
                      onCheckedChange={(c) => setEditingEmployee({
                        ...editingEmployee, 
                        permissions: {
                          ...editingEmployee.permissions, 
                          settings: c,
                          rooms: c ? editingEmployee.permissions?.rooms : false,
                          packages: c ? editingEmployee.permissions?.packages : false,
                          price_list: c ? editingEmployee.permissions?.price_list : false,
                          trainers: c ? editingEmployee.permissions?.trainers : false,
                          employees: c ? editingEmployee.permissions?.employees : false
                        }
                      })}
                    />
                  </div>
                  <div className={`ml-6 space-y-1.5 ${!editingEmployee.is_manager || editingEmployee.permissions?.settings === false ? 'opacity-50' : ''}`}>
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">{t('common:rooms')}</span>
                      <Switch
                        checked={editingEmployee.permissions?.rooms || false}
                        disabled={!editingEmployee.is_manager || editingEmployee.permissions?.settings === false}
                        onCheckedChange={(c) => setEditingEmployee({
                          ...editingEmployee, 
                          permissions: {...editingEmployee.permissions, rooms: c}
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">Paketler</span>
                      <Switch
                        checked={editingEmployee.permissions?.packages || false}
                        disabled={!editingEmployee.is_manager || editingEmployee.permissions?.settings === false}
                        onCheckedChange={(c) => setEditingEmployee({
                          ...editingEmployee, 
                          permissions: {...editingEmployee.permissions, packages: c}
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">Fiyat Listeleri</span>
                      <Switch
                        checked={editingEmployee.permissions?.price_list || false}
                        disabled={!editingEmployee.is_manager || editingEmployee.permissions?.settings === false}
                        onCheckedChange={(c) => setEditingEmployee({
                          ...editingEmployee, 
                          permissions: {...editingEmployee.permissions, price_list: c}
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">{t('common:trainers')}</span>
                      <Switch
                        checked={editingEmployee.permissions?.trainers || false}
                        disabled={!editingEmployee.is_manager || editingEmployee.permissions?.settings === false}
                        onCheckedChange={(c) => setEditingEmployee({
                          ...editingEmployee, 
                          permissions: {...editingEmployee.permissions, trainers: c}
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">{t('employees:employees')}</span>
                      <Switch
                        checked={editingEmployee.permissions?.employees || false}
                        disabled={!editingEmployee.is_manager || editingEmployee.permissions?.settings === false}
                        onCheckedChange={(c) => setEditingEmployee({
                          ...editingEmployee, 
                          permissions: {...editingEmployee.permissions, employees: c}
                        })}
                      />
                    </div>
                  </div>
                </div>

                {/* Ayarlar Sayfası - alt yetkilendirme (Yönetici-only) */}
                <div className="border rounded-xl p-3 space-y-2 border-amber-200 bg-amber-50/30" data-testid="settings-page-permissions-block-employee">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-navy flex items-center gap-2">
                      <Layers className="w-4 h-4" /> {t("employees:settingsPagePermissions", "Ayarlar Sayfası")}
                      <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t("employees:manager")}</span>
                    </span>
                  </div>
                  <p className="text-xs text-grey">{t("employees:settingsPagePermissionsHint", "Sol menüdeki Ayarlar sayfasında hangi bölümleri görebileceğini seçin.")}</p>
                  <div className={`ml-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5 ${!editingEmployee.is_manager ? "opacity-50" : ""}`}>
                    {[
                      { k: "settings_business_info", label: t("settings:businessInfo") },
                      { k: "settings_pricing", label: t("settings:cardPricesTitle") },
                      { k: "settings_accruals", label: t("settings:accrualDaysTitle") },
                      { k: "settings_working_hours", label: t("settings:workingHoursTitle") },
                      { k: "settings_branches", label: t("settings:branchesTitle") },
                      { k: "settings_training_types", label: t("settings:trainingTypesTitle") },
                      { k: "settings_measurements", label: t("settings:measurementParamsTitle") },
                      { k: "settings_expense_types", label: t("settings:expenseTypesTitle") },
                      { k: "settings_numbering", label: t("employees:numberingSettings", "Numaralandırma") },
                      { k: "settings_currency", label: t("common:currency") },
                      { k: "member_no_edit", label: t("employees:memberNoEdit", "Üye No Düzenleme") },
                      { k: "package_no_edit", label: t("employees:packageNoEdit", "Paket No Düzenleme") },
                    ].map((it) => (
                      <div key={it.k} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm" data-testid={`perm-${it.k}-employee`}>
                        <span className="text-grey">{it.label}</span>
                        <Switch
                          checked={editingEmployee.permissions?.[it.k] || false}
                          disabled={!editingEmployee.is_manager}
                          onCheckedChange={(c) => setEditingEmployee({ ...editingEmployee, permissions: { ...editingEmployee.permissions, [it.k]: c } })}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* İletişim - Ana toggle ile açılıp kapanabilir */}
                <div className="border rounded-xl p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MessageCircle className="w-4 h-4 text-navy" />
                      <span className="font-medium text-navy">{t('business:contact')}</span>
                    </div>
                    <Switch
                      checked={editingEmployee.permissions?.communication !== false}
                      onCheckedChange={(c) => {
                        // Ana toggle - alt izinleri de etkiler
                        setEditingEmployee({
                          ...editingEmployee, 
                          permissions: {
                            ...editingEmployee.permissions, 
                            communication: c,
                            messages: c ? editingEmployee.permissions?.messages : false,
                            ratings: c ? editingEmployee.permissions?.ratings : false,
                            surveys: c ? editingEmployee.permissions?.surveys : false,
                            events: c ? editingEmployee.permissions?.events : false,
                            evaluations: c ? editingEmployee.permissions?.evaluations : false,
                            goals: c ? editingEmployee.permissions?.goals : false
                          }
                        });
                      }}
                    />
                  </div>
                  <div className={`space-y-1.5 ml-2 ${!editingEmployee.permissions?.communication && editingEmployee.permissions?.communication !== undefined ? 'opacity-50' : ''}`}>
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">{t('common:messages')}</span>
                      <Switch
                        checked={editingEmployee.permissions?.messages !== false}
                        disabled={editingEmployee.permissions?.communication === false}
                        onCheckedChange={(c) => setEditingEmployee({
                          ...editingEmployee, 
                          permissions: {...editingEmployee.permissions, messages: c}
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">{t('business:evaluations')}</span>
                      <Switch
                        checked={editingEmployee.permissions?.ratings || false}
                        disabled={editingEmployee.permissions?.communication === false}
                        onCheckedChange={(c) => setEditingEmployee({
                          ...editingEmployee, 
                          permissions: {...editingEmployee.permissions, ratings: c}
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">Anketler</span>
                      <Switch
                        checked={editingEmployee.permissions?.surveys || false}
                        disabled={editingEmployee.permissions?.communication === false}
                        onCheckedChange={(c) => setEditingEmployee({
                          ...editingEmployee, 
                          permissions: {...editingEmployee.permissions, surveys: c}
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">Etkinlikler</span>
                      <Switch
                        checked={editingEmployee.permissions?.events || false}
                        disabled={editingEmployee.permissions?.communication === false}
                        onCheckedChange={(c) => setEditingEmployee({
                          ...editingEmployee, 
                          permissions: {...editingEmployee.permissions, events: c}
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">{t("evaluations:memberEvaluations")}</span>
                      <Switch
                        checked={editingEmployee.permissions?.evaluations || false}
                        disabled={editingEmployee.permissions?.communication === false}
                        onCheckedChange={(c) => setEditingEmployee({
                          ...editingEmployee, 
                          permissions: {...editingEmployee.permissions, evaluations: c}
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-grey">Hedefler & Rozetler</span>
                      <Switch
                        checked={editingEmployee.permissions?.goals || false}
                        disabled={editingEmployee.permissions?.communication === false}
                        onCheckedChange={(c) => setEditingEmployee({
                          ...editingEmployee, 
                          permissions: {...editingEmployee.permissions, goals: c}
                        })}
                      />
                    </div>
                  </div>
                </div>

                {/* Gelir Görme İzni */}
                <div className="border rounded-xl p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-navy" />
                      <span className="font-medium text-navy">{t("reports:viewRevenueInfo")}</span>
                      <span className="text-xs text-blue-600 bg-blue-100 px-1 rounded">{t("common:private")}</span>
                    </div>
                    <Switch
                      checked={editingEmployee.permissions?.can_view_revenue || false}
                      onCheckedChange={(c) => setEditingEmployee({
                        ...editingEmployee, 
                        permissions: {...editingEmployee.permissions, can_view_revenue: c}
                      })}
                    />
                  </div>
                  <p className="text-xs text-grey mt-2">{t("reports:ifPermOpenCanSeeIncome")}</p>
                </div>

                {/* Manuel Rezervasyon Tamamlama İzni - Sadece Yöneticilere Göster */}
                {editingEmployee.is_manager && (
                  <div className="border rounded-xl p-3 border-amber-200 bg-amber-50/50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-amber-600" />
                        <span className="font-medium text-navy">Manuel Rezervasyon Tamamlama</span>
                        <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                      </div>
                      <Switch
                        checked={editingEmployee.permissions?.manual_reservation_complete || false}
                        onCheckedChange={(c) => setEditingEmployee({
                          ...editingEmployee, 
                          permissions: {...editingEmployee.permissions, manual_reservation_complete: c}
                        })}
                      />
                    </div>
                    <p className="text-xs text-grey mt-2">{t("employees:ifPermOpenCanMarkComplete")}</p>
                  </div>
                )}

                {/* Manuel Rezervasyon Onaylama İzni - Sadece Yöneticilere Göster */}
                {editingEmployee.is_manager && (
                  <div className="border rounded-xl p-3 border-amber-200 bg-amber-50/50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-amber-600" />
                        <span className="font-medium text-navy">Manuel Rezervasyon Onaylama</span>
                        <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                      </div>
                      <Switch
                        checked={editingEmployee.permissions?.manual_reservation_approve || false}
                        onCheckedChange={(c) => setEditingEmployee({
                          ...editingEmployee, 
                          permissions: {...editingEmployee.permissions, manual_reservation_approve: c}
                        })}
                      />
                    </div>
                    <p className="text-xs text-grey mt-2">{t("employees:skipApprovalHint")}</p>
                  </div>
                )}

                {/* Geçmiş Tarihli Rezervasyon İzni - Sadece Yöneticilere Göster */}
                {editingEmployee.is_manager && (
                  <div className="border rounded-xl p-3 border-amber-200 bg-amber-50/50" data-testid="perm-backdate_reservations-employee-block">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CalendarIcon className="w-4 h-4 text-amber-600" />
                        <span className="font-medium text-navy">{t("employees:backdateReservations", "Geçmiş Tarihli Rezervasyon")}</span>
                        <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                      </div>
                      <Switch
                        checked={editingEmployee.permissions?.backdate_reservations !== false}
                        onCheckedChange={(c) => setEditingEmployee({
                          ...editingEmployee, 
                          permissions: {...editingEmployee.permissions, backdate_reservations: c}
                        })}
                        data-testid="perm-backdate_reservations-employee"
                      />
                    </div>
                    <p className="text-xs text-grey mt-2">Açıksa: Yönetici, "Min. Önceden Rezervasyon" süresine bakmaksızın geçmiş tarihli rezervasyon oluşturabilir.</p>
                  </div>
                )}

                {/* Ders Açıklaması Düzenleme İzni - Sadece Yöneticilere Göster */}
                {editingEmployee.is_manager && (
                  <div className="border rounded-xl p-3 border-amber-200 bg-amber-50/50" data-testid="perm-lesson_note_edit-employee-block">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileTextIcon className="w-4 h-4 text-amber-600" />
                        <span className="font-medium text-navy">{t("employees:lessonNoteEdit", "Ders Açıklaması Düzenleme")}</span>
                        <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                      </div>
                      <Switch
                        checked={editingEmployee.permissions?.lesson_note_edit !== false}
                        onCheckedChange={(c) => setEditingEmployee({
                          ...editingEmployee, 
                          permissions: {...editingEmployee.permissions, lesson_note_edit: c}
                        })}
                        data-testid="perm-lesson_note_edit-employee"
                      />
                    </div>
                    <p className="text-xs text-grey mt-2">Açıksa: Tamamlanan derslere açıklama (not) ekleyebilir ve sonradan düzenleyebilir.</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" className="rounded-xl" onClick={() => setShowEditModal(false)}>{t('common:cancel')}</Button>
            <Button className="bg-navy hover:bg-navy/90 rounded-xl" onClick={handleUpdateEmployee} disabled={isSavingEdit}>
              {isSavingEdit ? "Kaydediliyor..." : t('common:save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Modal */}
      <Dialog open={showAddModal} onOpenChange={(open) => { setShowAddModal(open); if (!open) resetAddForm(); }}>
        <DialogContent className="max-w-xl rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Users className="w-5 h-5" /> {t("employees:addNewEmployee")}</DialogTitle>
            <DialogDescription>
              {addEmployeeMode === "invitation" 
                ? t("employees:employeeInviteCodeDesc")
                : t("employees:addUnregisteredEmployeeDesc")
              }
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleAddEmployee} className="space-y-4 mt-4">
            {/* Mode Selection Tabs - Hidden for now, code preserved for future use */}
            {false && (
            <div className="flex border rounded-xl overflow-hidden">
              <button
                type="button"
                onClick={() => {
                  setAddEmployeeMode("invitation");
                  setValidatedUser(null);
                  setInvitationCode("");
                }}
                className={`flex-1 py-2.5 px-4 text-sm font-medium transition-colors ${
                  addEmployeeMode === "invitation" 
                    ? "bg-navy text-white" 
                    : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                }`}
              >
                {t("admin:withInvitationCode2")}
              </button>
              <button
                type="button"
                onClick={() => {
                  setAddEmployeeMode("unregistered");
                  setValidatedUser(null);
                  setInvitationCode("");
                }}
                className={`flex-1 py-2.5 px-4 text-sm font-medium transition-colors ${
                  addEmployeeMode === "unregistered" 
                    ? "bg-navy text-white" 
                    : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                }`}
              >
                {t("employees:unregisteredEmployee")}
              </button>
            </div>
            )}

            {addEmployeeMode === "invitation" ? (
              <>
                {/* Invitation Code Input - KVKK Uyumlu */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Key className="w-4 h-4" />
                    {t("admin:invitationCodeRequired")}
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder={t("admin:sixDigitCode2")}
                      value={invitationCode}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setInvitationCode(value);
                        if (value.length < 6) {
                          setValidatedUser(null);
                          setSelectedUser(null);
                        }
                      }}
                      className="text-center text-xl font-mono tracking-[0.3em] rounded-xl"
                      maxLength={6}
                      disabled={validatedUser !== null}
                      data-testid="employee-invitation-code-input"
                    />
                    {!validatedUser ? (
                      <Button
                        type="button"
                        onClick={validateInvitationCode}
                        disabled={invitationCode.length !== 6 || validatingCode}
                        className="bg-navy hover:bg-navy/90 rounded-xl px-6"
                        data-testid="validate-employee-code-btn"
                      >
                        {validatingCode ? "..." : t('members:verify')}
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setValidatedUser(null);
                          setInvitationCode("");
                          setSelectedUser(null);
                        }}
                        className="rounded-xl px-4"
                      >
                        {t("common:change")}
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    {t("employees:employeeCanGetCode")}
                  </p>
                </div>
                
                {/* Validated User Display */}
                {validatedUser && (
                  <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-xl">
                    <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-bold">
                      {validatedUser.name?.[0]}{validatedUser.surname?.[0]}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-green-800">
                        {validatedUser.name} {validatedUser.surname}
                      </p>
                      <p className="text-sm text-green-600">{validatedUser.email}</p>
                      {validatedUser.phone && (
                        <p className="text-xs text-green-600">{validatedUser.phone}</p>
                      )}
                    </div>
                    <CheckCircle2 className="w-6 h-6 text-green-500" />
                  </div>
                )}
              </>
            ) : (
              /* Unregistered Employee Form */
              <div className="space-y-4">
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                  {t("employees:unregisteredEmployeeNote")}
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Ad <span className="text-red-500">*</span></Label>
                    <Input
                      placeholder={t("employees:ph_calisanAdi")}
                      value={unregisteredEmployee.name}
                      onChange={(e) => setUnregisteredEmployee({...unregisteredEmployee, name: e.target.value})}
                      className="rounded-xl"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>{t('common:surname')}</Label>
                    <Input
                      placeholder={t("employees:ph_calisanSoyadi")}
                      value={unregisteredEmployee.surname}
                      onChange={(e) => setUnregisteredEmployee({...unregisteredEmployee, surname: e.target.value})}
                      className="rounded-xl"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>{t('common:phone')}</Label>
                    <Input
                      placeholder="05XX XXX XX XX"
                      value={unregisteredEmployee.phone}
                      onChange={(e) => setUnregisteredEmployee({...unregisteredEmployee, phone: e.target.value})}
                      className="rounded-xl"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>{t('common:email')}</Label>
                    <Input
                      type="email"
                      placeholder="ornek@email.com"
                      value={unregisteredEmployee.email}
                      onChange={(e) => setUnregisteredEmployee({...unregisteredEmployee, email: e.target.value})}
                      className="rounded-xl"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>{t('members:birthDate')}</Label>
                    <DateInputTR value={unregisteredEmployee.birth_date}
           onChange={(e) => setUnregisteredEmployee({...unregisteredEmployee, birth_date: e.target.value})}
           className="rounded-xl" />
                  </div>
                  <div className="space-y-1">
                    <Label>{t('common:gender')}</Label>
                    <Select 
                      value={unregisteredEmployee.gender} 
                      onValueChange={(val) => setUnregisteredEmployee({...unregisteredEmployee, gender: val})}
                    >
                      <SelectTrigger className="rounded-xl">
                        <SelectValue placeholder={t("common:ph_secin")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">{t('common:male')}</SelectItem>
                        <SelectItem value="female">{t('common:female')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Work Type Selection - Show when user validated OR unregistered mode */}
            {(validatedUser || addEmployeeMode === "unregistered") && (
            <>
            <div className="border-t pt-4 space-y-3">
              <h4 className="font-semibold text-navy flex items-center gap-2">
                <Banknote className="w-4 h-4" />
                {t("common:workType")}
              </h4>
              <Select value={newWorkType} onValueChange={setNewWorkType}>
                <SelectTrigger className="rounded-xl"><SelectValue placeholder={t("common:ph_calismaSekliSecin")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="salary_receiving" className="text-green-700">
                    <div className="flex items-center gap-2"><span className="text-green-600">💵</span> {t("employees:salaryReceiving")}</div>
                  </SelectItem>
                  <SelectItem value="salary_and_commission" className="text-green-700">
                    <div className="flex items-center gap-2"><span className="text-green-600">💎</span> {t("reports:salaryAndCommission")}</div>
                  </SelectItem>
                  <SelectItem value="commission_only" className="text-green-700">
                    <div className="flex items-center gap-2"><span className="text-green-600">💰</span> {t("employees:commissionOnly")}</div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-blue-600 bg-blue-50 p-2 rounded-lg flex items-start gap-2">
                <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                {workTypeInfo[newWorkType]}
              </p>
            </div>

            {/* Position */}
            <div>
              <Label>{t('common:position')}</Label>
              <Input value={newPosition} onChange={(e) => setNewPosition(e.target.value)} className="rounded-xl mt-1" placeholder="Resepsiyonist, Temizlik vs." />
            </div>

            {/* İşe Başlama Tarihi - Always visible */}
            <div className="p-4 bg-green-50 rounded-xl border border-green-200">
              <h4 className="font-semibold text-green-800 flex items-center gap-2 mb-3">
                <Calendar className="w-4 h-4" /> {t("common:workStartDate")} *
              </h4>
              <DatePickerTR
                value={newStartDate}
                onChange={(date) => setNewStartDate(date ? formatDateToLocal(date) : "")}
                placeholder={t("common:ph_i̇seBaslamaTarihiSecin")}
              />
              <p className="text-xs text-green-600 mt-2">{t("reports:workStartDateNote")}</p>
            </div>

            {/* Salary Section */}
            {newWorkType !== "commission_only" && (
              <div className="p-4 bg-slate-50 rounded-xl space-y-4 border">
                <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                  <Banknote className="w-4 h-4" /> {t("employees:salarySettings")} *
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">{t("employees:salaryAmount")}</Label>
                    <Input type="number" value={newSalary} onChange={(e) => setNewSalary(e.target.value)} className="rounded-xl mt-1" placeholder="0" />
                  </div>
                  <div>
                    <Label className="text-sm">{t("common:accruedStartDate")}</Label>
                    <DatePickerTR
                      value={newSalaryStartDate}
                      onChange={(date) => setNewSalaryStartDate(date ? formatDateToLocal(date) : "")}
                      placeholder={t('common:selectDate')}
                      className="mt-1"
                    />
                    <p className="text-xs text-grey mt-1">{t("employees:i̇lkMaasHakedisininHesaplanacagiTarih")}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Commission Section */}
            {newWorkType !== "salary_receiving" && (
              <div className="p-4 bg-slate-50 rounded-xl space-y-4 border">
                <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" /> {t("reports:commissionSettings")}
                </h4>

                {/* Calculation Method */}
                <div className="p-3 bg-blue-50 rounded-xl space-y-2">
                  <Label className="font-medium text-blue-800 text-sm">{t('trainers:commissionCalculation')}</Label>
                  <div className="flex flex-col gap-2">
                    <label className="flex items-start gap-3 p-2 bg-white rounded-lg cursor-pointer border hover:border-blue-300 text-sm">
                      <input type="radio" name="calcMethod" checked={newCommissionSettings.calculation_method !== "per_package_sale"} onChange={() => setNewCommissionSettings(p => ({...p, calculation_method: "per_lesson"}))} className="mt-0.5" />
                      <div><p className="font-medium">📚 {t("common:perLessonLabel")}</p><p className="text-xs text-grey">{t("reports:separateCommPerLesson")}</p></div>
                    </label>
                    <label className="flex items-start gap-3 p-2 bg-white rounded-lg cursor-pointer border hover:border-blue-300 text-sm">
                      <input type="radio" name="calcMethod" checked={newCommissionSettings.calculation_method === "per_package_sale"} onChange={() => setNewCommissionSettings(p => ({...p, calculation_method: "per_package_sale", commission_scope: "all_lessons"}))} className="mt-0.5" />
                      <div><p className="font-medium">📦 {t("packages:perPackageSale")}</p><p className="text-xs text-grey">{t("packages:totalCommOnPackageSale")}</p></div>
                    </label>
                    {newCommissionSettings.calculation_method === "per_package_sale" && (
                      <div className="ml-6 p-2 bg-blue-100 rounded-lg">
                        <p className="text-xs text-blue-700">{t("packages:employeeReceivesCommFromAllSales")}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Card Commissions */}
                {businessAcceptedCards.length > 0 && (
                  <div className="space-y-2">
                    <Label className="font-medium text-amber-800 text-sm flex items-center gap-2">
                      <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                      {t("reports:cardCommissions")}
                    </Label>
                    {businessAcceptedCards.map(cardName => {
                      const cardConfig = newCommissionSettings.card_commissions?.[cardName] || { type: "percentage", rates: {} };
                      const cardPrice = businessCardPrices[cardName] || 0;
                      return (
                        <div key={cardName} className="p-3 bg-amber-50 rounded-xl space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-amber-700 text-sm">{cardName}</span>
                            <Select value={cardConfig.type || "percentage"} onValueChange={(v) => setNewCommissionSettings(prev => ({...prev, card_commissions: {...prev.card_commissions, [cardName]: { ...cardConfig, type: v }}}))}>
                              <SelectTrigger className="w-28 h-8 rounded-lg text-sm"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="percentage">{t("common:percentage")}</SelectItem>
                                <SelectItem value="fixed">{t("common:fixedAmount")}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {/* Kart fiyatı uyarısı - sabit prim için */}
                          {cardConfig.type === "fixed" && cardPrice <= 0 && (
                            <p className="text-xs text-red-500 bg-red-50 p-2 rounded">
                              ⚠️ {t("common:cardPriceNotSet")}
                            </p>
                          )}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {businessTrainingTypes.map(trainingType => {
                              const maxFixedValue = cardConfig.type === "fixed" ? cardPrice : undefined;
                              const currentValue = cardConfig.rates?.[trainingType] || "";
                              const isOverMax = cardConfig.type === "fixed" && cardPrice > 0 && parseFloat(currentValue) > cardPrice;
                              
                              return (
                              <div key={trainingType}>
                                <Label className="text-xs text-amber-600">{trainingType}</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  max={cardConfig.type === "percentage" ? 100 : maxFixedValue}
                                  step="1"
                                  value={currentValue}
                                  onChange={(e) => {
                                    const inputValue = e.target.value;
                                    if (inputValue === "" || /^\d*\.?\d*$/.test(inputValue)) {
                                      let value = inputValue === "" ? "" : parseFloat(inputValue) || 0;
                                      // Yüzde için max 100
                                      if (cardConfig.type === "percentage" && value > 100) {
                                        value = 100;
                                      }
                                      // Sabit için max kart fiyatı
                                      if (cardConfig.type === "fixed" && cardPrice > 0 && value > cardPrice) {
                                        value = cardPrice;
                                        toast.error(t("reports:commissionExceedsCardPrice"));
                                      }
                                      setNewCommissionSettings(prev => ({...prev, card_commissions: {...prev.card_commissions, [cardName]: {...cardConfig, rates: { ...cardConfig.rates, [trainingType]: value }}}}));
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    // Block non-numeric keys except navigation and decimal
                                    if (!/[\d.\-]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
                                      e.preventDefault();
                                    }
                                  }}
                                  className={`rounded-lg h-8 text-sm ${isOverMax ? 'border-red-500' : ''}`}
                                  placeholder={cardConfig.type === "fixed" ? (cardPrice > 0 ? `Max ${currencySymbol}${cardPrice}` : currencySymbol) : "% (0-100)"}
                                  disabled={cardConfig.type === "fixed" && cardPrice <= 0}
                                />
                                {isOverMax && (
                                  <p className="text-xs text-red-500 mt-0.5">Max: {currencySymbol}{cardPrice}</p>
                                )}
                              </div>
                            )})}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Package Commissions */}
                <div className="space-y-2">
                  <Label className="font-medium text-emerald-800 text-sm flex items-center gap-2">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                    {t("packages:packageCommissions")}
                  </Label>
                  <div className="p-3 bg-emerald-50 rounded-xl space-y-2">
                    <Select value={newCommissionSettings.package_commissions?.type || "percentage"} onValueChange={(v) => setNewCommissionSettings(prev => ({...prev, package_commissions: {...prev.package_commissions, type: v}}))}>
                      <SelectTrigger className="w-full h-8 rounded-lg text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">{t("common:percentage")}</SelectItem>
                        <SelectItem value="fixed">{t("common:fixedAmount")}</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {businessTrainingTypes.map(type => {
                        const packageType = newCommissionSettings.package_commissions?.type || "percentage";
                        return (
                          <div key={type}>
                            <Label className="text-xs text-emerald-600">{type}</Label>
                            <Input
                              type="number"
                              min="0"
                              max={packageType === "percentage" ? 100 : undefined}
                              step="1"
                              value={newCommissionSettings.package_commissions?.rates?.[type] || ""}
                              onChange={(e) => {
                                let value = e.target.value === "" ? "" : parseFloat(e.target.value) || 0;
                                // Yüzde için max 100
                                if (packageType === "percentage" && value > 100) {
                                  value = 100;
                                }
                                setNewCommissionSettings(prev => ({...prev, package_commissions: {...prev.package_commissions, rates: {...prev.package_commissions?.rates, [type]: value}}}));
                              }}
                              onKeyDown={(e) => {
                                // Block non-numeric keys except navigation and decimal
                                if (!/[\d.\-]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                              className="rounded-lg h-8 text-sm"
                              placeholder={packageType === "fixed" ? currencySymbol : "% (0-100)"}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}
            </>
            )}

            <DialogFooter className="gap-2 mt-4">
              <Button type="button" variant="outline" className="rounded-xl" onClick={() => setShowAddModal(false)}>{t('common:cancel')}</Button>
              <Button 
                type="submit" 
                className="bg-navy hover:bg-navy/90 rounded-xl" 
                disabled={isAddingEmployee || (addEmployeeMode === "invitation" && !validatedUser) || (addEmployeeMode === "unregistered" && !unregisteredEmployee.name.trim())}
              >
                {isAddingEmployee ? "Ekleniyor..." : t('common:add')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={!!deleteEmployeeId} onOpenChange={() => setDeleteEmployeeId(null)}>
        <DialogContent className="max-w-sm rounded-2xl">
          <DialogHeader>
            <DialogTitle>{t("employees:calisaniCikar")}</DialogTitle>
            <DialogDescription>{t("employees:removeEmployeeConfirm")}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-xl" onClick={() => setDeleteEmployeeId(null)}>{t('common:cancel')}</Button>
            <Button className="bg-red-600 hover:bg-red-700 rounded-xl" onClick={() => handleDeleteEmployee(deleteEmployeeId)}>{t("common:cikar")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* End Employment Modal */}
      <Dialog open={showEndModal} onOpenChange={setShowEndModal}>
        <DialogContent className="max-w-sm rounded-2xl">
          <DialogHeader>
            <DialogTitle>{t("employees:calisanAyrildi")}</DialogTitle>
            <DialogDescription>{t("common:markEmployeeAsLeftConfirm", { name: selectedEmployee?.name })}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-xl" onClick={() => setShowEndModal(false)}>{t('common:cancel')}</Button>
            <Button className="bg-red-600 hover:bg-red-700 rounded-xl" onClick={handleEndEmployment}>{t("common:markAsLeft")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bonus Payment Modal */}
      <Dialog open={showBonusModal} onOpenChange={setShowBonusModal}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Gift className="w-5 h-5" /> {t("common:extraPayment")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t("common:odemeTipi")}</Label>
              <Select value={bonusForm.payment_type} onValueChange={(v) => setBonusForm({...bonusForm, payment_type: v})}>
                <SelectTrigger className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bonus">Prim</SelectItem>
                  <SelectItem value="advance">Avans</SelectItem>
                  <SelectItem value="other">{t('common:other')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tutar ({currencySymbol}) *</Label>
              <Input type="number" value={bonusForm.amount} onChange={(e) => setBonusForm({...bonusForm, amount: e.target.value})} className="rounded-xl mt-1" placeholder="0" />
            </div>
            <div>
              <Label>{t('common:description')}</Label>
              <Input value={bonusForm.description} onChange={(e) => setBonusForm({...bonusForm, description: e.target.value})} className="rounded-xl mt-1" placeholder={t("common:paymentDescription")} />
            </div>
            <div>
              <Label>{t('common:date')}</Label>
              <DatePickerTR
                value={bonusForm.payment_date}
                onChange={(date) => setBonusForm({...bonusForm, payment_date: date ? formatDateToLocal(date) : ""})}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" className="rounded-xl" onClick={() => setShowBonusModal(false)}>{t('common:cancel')}</Button>
            <Button className="bg-navy hover:bg-navy/90 rounded-xl" onClick={handleAddBonus}>{t('common:save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Employee Schedule Modal */}
      <EmployeeScheduleModal
        isOpen={showScheduleModal}
        onClose={() => {
          setShowScheduleModal(false);
          setScheduleEmployee(null);
        }}
        employee={scheduleEmployee}
        businessId={businessId}
        businessWorkingHours={businessWorkingHours}
      />

      {/* Employee Time Block Modal */}
      <EmployeeTimeBlockModal
        isOpen={showTimeBlockModal}
        onClose={() => {
          setShowTimeBlockModal(false);
          setScheduleEmployee(null);
        }}
        employee={scheduleEmployee}
        businessId={businessId}
      />

      {/* Employee Commission Report Modal */}
      <EmployeeCommissionReportModal
        isOpen={showCommissionReportModal}
        onClose={() => {
          setShowCommissionReportModal(false);
          setScheduleEmployee(null);
        }}
        employee={scheduleEmployee}
        businessId={businessId}
      />

    </div>
  );
}
